import { z } from 'zod';

export interface ParameterOption {
  readonly id: string;
  readonly name: string;
  readonly unit: string;
  readonly min: number;
  readonly max: number;
  readonly decimalPlaces: number;
}

export const PARAMETER_OPTIONS: readonly ParameterOption[] = [
  { id: 'alkalinity', name: 'Alkalinity', unit: 'dKH', min: 0, max: 20, decimalPlaces: 1 },
  { id: 'calcium', name: 'Calcium', unit: 'ppm', min: 0, max: 600, decimalPlaces: 0 },
  { id: 'magnesium', name: 'Magnesium', unit: 'ppm', min: 0, max: 2000, decimalPlaces: 0 },
  { id: 'nitrate', name: 'Nitrate', unit: 'ppm', min: 0, max: 100, decimalPlaces: 2 },
  { id: 'phosphate', name: 'Phosphate', unit: 'ppm', min: 0, max: 1, decimalPlaces: 3 },
  { id: 'salinity', name: 'Salinity', unit: 'ppt', min: 0, max: 45, decimalPlaces: 1 },
  { id: 'temperature', name: 'Temperature', unit: '°C', min: 0, max: 40, decimalPlaces: 1 },
  { id: 'ph', name: 'pH', unit: '', min: 6, max: 9, decimalPlaces: 2 },
  { id: 'potassium', name: 'Potassium', unit: 'ppm', min: 0, max: 600, decimalPlaces: 0 },
  { id: 'iodine', name: 'Iodine', unit: 'ppm', min: 0, max: 0.1, decimalPlaces: 3 },
] as const;

/**
 * Validation of the single-measurement form. Messages are translation keys
 * (the form translates them). 0 is a real reading (e.g. nitrate, phosphate
 * below the test's range), so only negative values are rejected.
 */
export const ManualMeasurementFormSchema = z.object({
  parameterId: z.string().min(1, 'measurement.form.errors.parameterRequired'),
  value: z
    .number({ invalid_type_error: 'measurement.form.errors.valueInvalid' })
    .finite('measurement.form.errors.valueInvalid')
    .nonnegative('measurement.form.errors.valueNegative'),
  measuredAt: z.string().min(1, 'measurement.form.errors.measuredAtRequired'),
});

export type ManualMeasurementFormValues = z.infer<typeof ManualMeasurementFormSchema>;

// Shape of one reading and one saved measurement set, matching the backend
// response used by services/measurementsService.ts and consumed by the
// timeline/history views (useMeasurements, MeasurementHistoryTable).
export interface MeasurementReading {
  readonly parameterKey: string;
  readonly value: number;
  readonly unit: string;
}

export interface Measurement {
  readonly id: string;
  readonly aquariumId: string;
  readonly measuredAt: string;
  readonly source: string;
  readonly notes: string | null;
  readonly readings: readonly MeasurementReading[];
  readonly createdAt: string;
  readonly updatedAt: string;
}

// ---------------------------------------------------------------------------
// Per-parameter unit metadata for the multi-parameter test session entry form
// (see docs/design/issue-1740/spec.md). Purely additive: provides the
// canonical storage unit, the set of units that may be displayed/selected for
// that parameter, and the default numeric precision/step used by the input
// control. Derived verbatim from the existing `unit` and `decimalPlaces`
// fields on PARAMETER_OPTIONS above — no new unit strings or conversion
// behaviour are introduced here.
export interface ParameterUnitMetadata {
  /** The unit values are stored/displayed in today — copied verbatim from PARAMETER_OPTIONS.unit. */
  readonly canonicalUnit: string;
  /** Units selectable for display for this parameter; always includes canonicalUnit. */
  readonly supportedUnits: readonly string[];
  /** Default number of decimal places shown/entered for this parameter. */
  readonly defaultPrecision: number;
  /** Default HTML number input `step` value matching defaultPrecision. */
  readonly defaultStep: string;
}

/**
 * Builds the HTML number input `step` string for a given decimal precision,
 * e.g. 0 -> '1', 1 -> '0.1', 2 -> '0.01', 3 -> '0.001'.
 */
function stepForPrecision(precision: number): string {
  return precision <= 0 ? '1' : `0.${'0'.repeat(precision - 1)}1`;
}

// One entry per existing PARAMETER_OPTIONS id. canonicalUnit is copied
// verbatim from the corresponding PARAMETER_OPTIONS.unit value so this is a
// true no-behaviour-change addition; defaultPrecision mirrors each
// parameter's existing decimalPlaces value.
export type ParameterId = (typeof PARAMETER_OPTIONS)[number]['id'];

export const PARAMETER_UNIT_METADATA: Record<ParameterId, ParameterUnitMetadata> = {
  // Alkalinity: dKH is the only unit currently used in PARAMETER_OPTIONS/ManualMeasurementForm.
  alkalinity: {
    canonicalUnit: 'dKH',
    supportedUnits: ['dKH'],
    defaultPrecision: 1,
    defaultStep: stepForPrecision(1),
  },
  // Calcium: ppm, displayed as a whole number today (decimalPlaces: 0).
  calcium: {
    canonicalUnit: 'ppm',
    supportedUnits: ['ppm'],
    defaultPrecision: 0,
    defaultStep: stepForPrecision(0),
  },
  // Magnesium: ppm, displayed as a whole number today (decimalPlaces: 0).
  magnesium: {
    canonicalUnit: 'ppm',
    supportedUnits: ['ppm'],
    defaultPrecision: 0,
    defaultStep: stepForPrecision(0),
  },
  // Nitrate: ppm, 2 decimal places per existing PARAMETER_OPTIONS entry.
  nitrate: {
    canonicalUnit: 'ppm',
    supportedUnits: ['ppm'],
    defaultPrecision: 2,
    defaultStep: stepForPrecision(2),
  },
  // Phosphate: ppm, 3 decimal places per existing PARAMETER_OPTIONS entry.
  phosphate: {
    canonicalUnit: 'ppm',
    supportedUnits: ['ppm'],
    defaultPrecision: 3,
    defaultStep: stepForPrecision(3),
  },
  // Salinity: ppt is the unit currently used in PARAMETER_OPTIONS; no SG
  // conversion is introduced here (that belongs to the backend unit
  // conversion module in a separate sub-issue).
  salinity: {
    canonicalUnit: 'ppt',
    supportedUnits: ['ppt'],
    defaultPrecision: 1,
    defaultStep: stepForPrecision(1),
  },
  // Temperature: °C is the unit currently used; °F conversion is a backend
  // concern handled elsewhere and not implied by existing code here.
  temperature: {
    canonicalUnit: '°C',
    supportedUnits: ['°C'],
    defaultPrecision: 1,
    defaultStep: stepForPrecision(1),
  },
  // pH: unitless, 2 decimal places per existing PARAMETER_OPTIONS entry.
  ph: {
    canonicalUnit: '',
    supportedUnits: [''],
    defaultPrecision: 2,
    defaultStep: stepForPrecision(2),
  },
  // Potassium: ppm, displayed as a whole number today (decimalPlaces: 0).
  potassium: {
    canonicalUnit: 'ppm',
    supportedUnits: ['ppm'],
    defaultPrecision: 0,
    defaultStep: stepForPrecision(0),
  },
  // Iodine: ppm, 3 decimal places per existing PARAMETER_OPTIONS entry.
  iodine: {
    canonicalUnit: 'ppm',
    supportedUnits: ['ppm'],
    defaultPrecision: 3,
    defaultStep: stepForPrecision(3),
  },
};

/**
 * Looks up the unit metadata for a given parameter id. Returns `undefined`
 * for an unknown parameter id rather than throwing, so callers can decide
 * how to handle a parameter that has no metadata yet.
 */
export function getParameterUnitMetadata(parameterId: string): ParameterUnitMetadata | undefined {
  return Object.hasOwn(PARAMETER_UNIT_METADATA, parameterId)
    ? PARAMETER_UNIT_METADATA[parameterId as ParameterId]
    : undefined;
}

// ---------------------------------------------------------------------------
// Multi-parameter test session entry (issue #1740 / sub-issue #1775).
//
// These types mirror the real backend request/response shapes field-for-field:
//   - SessionDefaultParameter matches src/routes/measurementSessionDefaults.ts
//   - TestKitProfileResponse matches src/routes/testKitProfiles.ts
// No additional fields (e.g. parameterName, lastUnit) are invented here since
// the backend does not return them.

/**
 * One entry in the batch submitted to POST /measurements/session. The server
 * schema is strict and requires parameterName (CreateMeasurementSchema without
 * measuredAt). unit is the unit the value was entered in; the server converts
 * alkalinity (dKH, meq/L, ppm CaCO3), salinity (ppt, SG) and temperature
 * (°C, °F) to their canonical unit and rejects any other unit for them.
 */
export interface SessionEntryInput {
  parameterId: string;
  parameterName: string;
  value: number;
  unit: string;
  testKitProfileId?: string;
}

/** Request body for POST /api/v1/aquariums/:aquariumId/measurements/session. */
export interface SubmitMeasurementSessionInput {
  measuredAt: string;
  entries: SessionEntryInput[];
}

/** One persisted measurement row returned from a successful session submission. */
export interface SessionMeasurementResult {
  id: string;
  aquariumId: string;
  parameterId: string;
  parameterName: string;
  value: number;
  unit: string;
  measuredAt: string;
  createdAt: string;
}

/** A non-blocking "did you mean …?" hint for one entry, in the test-kit profile's unit. */
export interface SessionPlausibilityWarning {
  suggestion: number;
}

/** Response payload (the inner `data` of the envelope) for a successful session submission. */
export interface MeasurementSessionResult {
  sessionId: string;
  measurements: SessionMeasurementResult[];
  /** Keyed by the entry's index in the submitted `entries` array; entries without a hint are absent. */
  warnings: Record<string, SessionPlausibilityWarning>;
}

/**
 * Catalogue entry for a test-kit profile, returned by
 * GET /api/v1/test-kit-profiles. Field names match
 * src/routes/testKitProfiles.ts's TestKitProfileResponse exactly.
 */
export interface TestKitProfileResponse {
  id: string;
  brand: string;
  parameterId: string;
  precision: number;
  plausibleMin: number;
  plausibleMax: number;
  unit: string;
  createdAt: string;
  /** Guided test session: the kit's curated steps (null when none are known). */
  procedure?: TestKitProcedure | null;
}

/** One step of a test kit (src/types/testKitProcedure.ts); its text is `guided.step.<type>`. */
export interface ProcedureStep {
  type: 'rinse' | 'fill_sample' | 'zero_meter' | 'add_reagent' | 'swirl' | 'wait' | 'titrate' | 'read_value' | 'measure_meter';
  ml?: number;
  reagent?: string;
  amount?: number;
  unit?: 'drops' | 'scoop' | 'ml' | 'packet';
  /** Timer length of a wait, or the duration of a swirl. */
  seconds?: number;
}

export interface TestKitProcedure {
  version: number;
  steps: ProcedureStep[];
  /** Checked against the manufacturer's instructions; otherwise the page asks to compare with the kit. */
  verified: boolean;
}

/**
 * Pre-fill defaults for one parameter, returned by
 * GET /api/v1/aquariums/:aquariumId/measurements/session-defaults. Field
 * names match src/routes/measurementSessionDefaults.ts's
 * SessionDefaultParameter exactly.
 */
export interface SessionDefaultParameter {
  parameterId: string;
  lastValue: number | null;
  lastMeasuredAt: string | null;
  testKitProfileId: string | null;
  /** Guided test session: the user's own timer lengths for the kit, by step index. */
  waitOverrides?: Record<string, number> | null;
}

/** Response payload (the inner `data` of the envelope) for the session-defaults aggregate endpoint. */
export interface MeasurementSessionDefaultsResponse {
  parameters: SessionDefaultParameter[];
}
